import { Link } from '@tanstack/react-router'
import {
  BadgeCheck,
  CheckCircle2,
  FileSpreadsheet,
  KeyRound,
  Lock,
  QrCode,
  ScrollText,
  ShieldCheck,
  UserPlus,
  Wallet,
} from 'lucide-react'
import { motion, useScroll, useTransform } from 'motion/react'
import { useRef } from 'react'
import { SonCta } from '../components/cta'
import { Reveal, Stagger, StaggerItem, Marquee, YUMUSAK } from '../components/hareket'
import { Foto, FotoKart, YuzenKart } from '../components/foto'
import { Mockup } from '../components/mockup'
import { ModulKarti } from '../components/modul-karti'
import { SssAkordeon } from '../components/sss-akordeon'
import {
  Baslik,
  ButonLink,
  Container,
  DEMO_YOLU,
  Eyebrow,
  Kart,
  Rozet,
  Section,
  cn,
} from '../components/ui'
import { COZUMLER } from '../icerik/cozumler'
import { MODULLER, modulBul } from '../icerik/moduller'
import { PLANLAR, tl } from '../icerik/planlar'
import { GENEL_SSS } from '../icerik/sss'
import { useHead } from '../lib/head'

export function AnaSayfa() {
  useHead(
    {
      title: 'Akıllı Dernek',
      description:
        'Derneğinizin üyeleri, aidatı, web sitesi, kongresi ve eğitimi tek yerde. On dört gün deneme, kart bilgisi yok, Excel aktarımı ücretsiz.',
    },
    '/',
  )
  return (
    <>
      <Hero />
      <DernekTipleri />
      <TekYerde />
      <Derinlik />
      <NasilCalisir />
      <Guvenlik />
      <Cozumler />
      <FiyatOzeti />
      <Sss />
      <SonCta />
    </>
  )
}

/* ---------------- Hero ---------------- */
function Hero() {
  const ref = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] })
  const y = useTransform(scrollYProgress, [0, 1], [0, 60])

  return (
    <div ref={ref} className="relative overflow-hidden pt-28 sm:pt-36">
      <div className="glow pointer-events-none absolute inset-x-0 top-0 h-[520px]" aria-hidden />
      <div className="grid-bg pointer-events-none absolute inset-x-0 top-0 h-[640px]" aria-hidden />
      <Container className="relative">
        <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: YUMUSAK }}
            >
              <Eyebrow>Dernek yönetim platformu</Eyebrow>
            </motion.div>
            <motion.h1
              className="display mt-5 text-balance text-[2.6rem] leading-[1.02] text-ink sm:text-6xl lg:text-[4.1rem]"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: YUMUSAK, delay: 0.05 }}
            >
              Üyeleriniz, aidatınız, web siteniz ve kongreniz{' '}
              <span className="text-brand">tek yerde.</span>
            </motion.h1>
            <motion.p
              className="mt-6 max-w-xl text-pretty text-lg leading-relaxed text-ink-2"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: YUMUSAK, delay: 0.12 }}
            >
              Akıllı Dernek, derneğin bütün işini tek panelde toplar: başvurudan makbuza üyelik ve
              aidat, kendi alan adınızda web sitesi, üye portalı, kurullar ve genel kurul, bin
              kişilik kongre. Mevzuata uygun kayıt tutmanıza yardım eder; sizi yormaz.
            </motion.p>
            <motion.div
              className="mt-8 flex flex-col gap-3 sm:flex-row"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: YUMUSAK, delay: 0.2 }}
            >
              <ButonLink to={DEMO_YOLU} size="lg" ok>
                On dört gün ücretsiz deneyin
              </ButonLink>
              <ButonLink to="/ozellikler" variant="secondary" size="lg">
                Özellikleri inceleyin
              </ButonLink>
            </motion.div>
            <motion.ul
              className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-ink-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.35 }}
            >
              {['Kart bilgisi istenmez', 'Excel aktarımı ücretsiz', 'Tahsilattan komisyon yok'].map(
                (t) => (
                  <li key={t} className="flex items-center gap-1.5">
                    <CheckCircle2 className="size-4 text-brand" aria-hidden />
                    {t}
                  </li>
                ),
              )}
            </motion.ul>
          </div>

          <motion.div
            className="relative mx-auto w-full max-w-md lg:max-w-none"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.9, ease: YUMUSAK, delay: 0.25 }}
          >
            <FotoKart
              ustune={
                <>
                  <YuzenKartHareketli className="-left-4 top-12 sm:-left-10" gecikme={0.9}>
                    <Kesit
                      Ikon={Wallet}
                      baslik="Tahsilat alındı"
                      metin="Ayşe Yılmaz · 1.200 ₺ · makbuz gönderildi"
                    />
                  </YuzenKartHareketli>
                  <YuzenKartHareketli className="-right-4 top-1/2 sm:-right-8" gecikme={1.2}>
                    <Kesit
                      Ikon={UserPlus}
                      baslik="Yeni başvuru"
                      metin="Zeynep Kaya · onay bekliyor"
                    />
                  </YuzenKartHareketli>
                  <YuzenKartHareketli className="-left-4 bottom-10 sm:-left-8" gecikme={1.5}>
                    <div className="w-44">
                      <div className="flex justify-between text-[11px] text-ink-3">
                        <span>2026 aidatı</span>
                        <span className="data">%86</span>
                      </div>
                      <div className="mt-1.5 h-1.5 rounded-[3px] bg-line">
                        <motion.i
                          className="block h-full rounded-[3px] bg-brand"
                          initial={{ width: 0 }}
                          animate={{ width: '86%' }}
                          transition={{ duration: 1.2, ease: YUMUSAK, delay: 1.8 }}
                        />
                      </div>
                      <p className="data mt-1.5 text-xs font-semibold text-ink">
                        1.104 / 1.284 üye ödedi
                      </p>
                    </div>
                  </YuzenKartHareketli>
                </>
              }
            >
              <Foto
                ad="hero"
                oran="3:4"
                oncelik
                sizes="(min-width: 1024px) 520px, (min-width: 640px) 448px, 100vw"
                alt="Aydınlık bir dernek ofisinde dizüstü bilgisayarında çalışan, gülümseyen dernek sekreteri"
              />
            </FotoKart>
          </motion.div>
        </div>

        <motion.div
          className="relative mx-auto mt-16 max-w-5xl sm:mt-24"
          style={{ y }}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-10% 0px' }}
          transition={{ duration: 0.9, ease: YUMUSAK }}
        >
          <p className="mb-4 text-center text-[11px] font-medium uppercase tracking-[0.14em] text-ink-3">
            Yönetim paneli · özet ekranı
          </p>
          <Mockup anahtar="ozet" />
        </motion.div>
      </Container>
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-page to-transparent"
        aria-hidden
      />
    </div>
  )
}

/** Fotoğraf üstünde hafifçe süzülen kart: belirir, sonra yavaşça salınır. */
function YuzenKartHareketli({
  className,
  gecikme,
  children,
}: {
  className?: string
  gecikme: number
  children: React.ReactNode
}) {
  return (
    <motion.div
      className={cn('absolute z-10', className)}
      initial={{ opacity: 0, y: 12, scale: 0.96 }}
      animate={{ opacity: 1, y: [0, -6, 0], scale: 1 }}
      transition={{
        opacity: { delay: gecikme, duration: 0.5 },
        scale: { delay: gecikme, duration: 0.5 },
        y: { delay: gecikme, duration: 5, repeat: Infinity, ease: 'easeInOut' },
      }}
    >
      <YuzenKart className="static">{children}</YuzenKart>
    </motion.div>
  )
}

function Kesit({
  Ikon,
  baslik,
  metin,
}: {
  Ikon: React.ComponentType<{ className?: string }>
  baslik: string
  metin: string
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="grid size-8 shrink-0 place-items-center rounded-full bg-brand-subtle text-brand">
        <Ikon className="size-4" />
      </span>
      <span>
        <span className="block text-xs font-semibold text-ink">{baslik}</span>
        <span className="block text-[11px] text-ink-3">{metin}</span>
      </span>
    </div>
  )
}

/* ---------------- Dernek tipleri şeridi ---------------- */
function DernekTipleri() {
  const tipler = [
    'Meslek dernekleri',
    'Federasyonlar',
    'Mezun dernekleri',
    'Hemşehri dernekleri',
    'Spor kulüpleri',
    'Vakıflar',
    'Eğitim dernekleri',
    'Sanayici ve iş insanı dernekleri',
    'Kültür ve sanat dernekleri',
    'Yardımlaşma dernekleri',
    'Sağlık dernekleri',
    'Mühendis odaları ve birlikler',
  ]
  return (
    <div className="border-y border-line bg-surface/60 py-6">
      <Container>
        <p className="text-center text-xs font-medium uppercase tracking-[0.14em] text-ink-3">
          Her ölçekte dernek, vakıf ve federasyon için
        </p>
      </Container>
      <Marquee className="mt-4" saniye={48}>
        {tipler.map((t) => (
          <span key={t} className="whitespace-nowrap text-sm font-medium text-ink-2">
            {t}
          </span>
        ))}
      </Marquee>
    </div>
  )
}

/* ---------------- Tek yerde ---------------- */
function TekYerde() {
  return (
    <Section id="moduller">
      <Reveal>
        <Baslik
          eyebrow="Tek yerde"
          baslik="Derneğin bütün işi, birbirini tanıyan modüllerle"
          metin="Üye kaydı aidatı bilir, aidat kongre biletinin fiyatını belirler, tahsilat makbuza ve gelir tablosuna düşer. Ayrı programlar arasında liste taşımazsınız."
          hizala="center"
        />
      </Reveal>
      <Stagger className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {MODULLER.map((m) => (
          <StaggerItem key={m.slug} className="h-full">
            <ModulKarti modul={m} />
          </StaggerItem>
        ))}
      </Stagger>
    </Section>
  )
}

/* ---------------- Derinlik: üç ana akış ---------------- */
function Derinlik() {
  const bloklar = [
    {
      modul: modulBul('aidat-ve-tahsilat'),
      eyebrow: 'Aidat ve tahsilat',
      baslik: 'Sayman hiçbir şey yapmadan borç oluşur, hatırlatılır, tahsil edilir.',
      metin:
        'Tarifeyi bir kez tanımlarsınız. Dönem başında borç kendiliğinden oluşur, vade yaklaşınca üyeye e-posta ve SMS gider, üye portaldan kartıyla öder, makbuz aynı dakika e-postasındadır. Havale dekontu açıklamadaki numarayla eşleşir.',
      maddeler: [
        'PayTR, İyzico, Kuveyt Türk, Param',
        'Tahsilattan komisyon yok',
        'Kuruş hassasiyetinde hesap',
      ],
      foto: 'aidat' as const,
      alt: 'Pencere önündeki masasında aidat listesini kalemle inceleyen dernek saymanı',
      kesitler: (
        <>
          <YuzenKart className="-left-3 top-6 sm:-left-8">
            <Kesit Ikon={Wallet} baslik="Hatırlatma gönderildi" metin="61 üye · vade 31 Ocak" />
          </YuzenKart>
          <YuzenKart className="-right-3 bottom-8 sm:-right-8">
            <div className="w-48">
              <div className="flex justify-between text-[11px] text-ink-3">
                <span>Ocak tahsilatı</span>
                <span className="data">184.600 ₺</span>
              </div>
              <div className="mt-1.5 h-1.5 rounded-[3px] bg-line">
                <motion.i
                  className="block h-full rounded-[3px] bg-brand"
                  initial={{ width: 0 }}
                  whileInView={{ width: '86%' }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, ease: YUMUSAK, delay: 0.4 }}
                />
              </div>
              <p className="mt-1.5 text-xs text-ink-2">
                Makbuz <span className="data font-semibold text-ink">2026-0412</span> kesildi
              </p>
            </div>
          </YuzenKart>
        </>
      ),
    },
    {
      modul: modulBul('etkinlik-ve-kongre'),
      eyebrow: 'Kongre',
      baslik: 'Bin kişilik kongreyi üç kişilik ekip yönetir.',
      metin:
        'Bilet, otel, transfer ve gala tek sepette. Erken kayıt fiyatı tarihle kendiliğinden değişir, oda arkadaşı eşleşmeleri sistemden çıkar, kapıda görevli telefonuyla QR okutur, sertifikalar yoklamaya göre otomatik gider. Etkinlik başına ücret yok.',
      maddeler: [
        'Konaklama ve oda arkadaşı',
        'QR kapı kontrolü, çevrimdışı çalışır',
        'Sponsor, stant, bildiri süreci',
      ],
      foto: 'kongre' as const,
      alt: 'Kongre kayıt masasında görevlinin katılımcının telefonundaki QR kodu okutması',
      ters: true,
      kesitler: (
        <>
          <YuzenKart className="-left-3 bottom-8 sm:-left-8">
            <Kesit
              Ikon={QrCode}
              baslik="Giriş tamam · Ayşe Yılmaz"
              metin="A salonu · bugün 418 giriş, 0 mükerrer"
            />
          </YuzenKart>
          <YuzenKart className="-right-3 top-6 sm:-right-8">
            <div className="grid grid-cols-3 gap-3 text-center">
              {[
                ['Kayıt', '612'],
                ['Otel', '340'],
                ['Transfer', '188'],
              ].map(([l, v]) => (
                <div key={l}>
                  <p className="text-[10px] text-ink-3">{l}</p>
                  <p className="data text-sm font-semibold text-ink">{v}</p>
                </div>
              ))}
            </div>
          </YuzenKart>
        </>
      ),
    },
    {
      modul: modulBul('kurullar-ve-genel-kurul'),
      eyebrow: 'Genel kurul',
      baslik: 'Çağrıdan tutanağa genel kurul, salonda ve uzaktan.',
      metin:
        'Hazirun listesi aidat durumundan otomatik çıkar, kapıda QR ile yoklama alınır, çoğunluk sağlandığı anda ekranda görünür. Uzaktan katılanlar canlı oturuma bağlanır; oylama salonla aynı anda açılır, kayıt tutanağın dayanağı olarak saklanır.',
      maddeler: [
        'Karar defteri, sıra numarası geri alınmaz',
        'Açık ve gizli elektronik oylama',
        'Sonuç bildirimi için hazır döküm',
      ],
      foto: 'genel-kurul' as const,
      alt: 'Genel kurul salonunda el kaldırarak oy kullanan üyeler ve öndeki divan masası',
      kesitler: (
        <>
          <YuzenKart className="-right-3 top-6 sm:-right-8">
            <div className="w-48">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-ink">Gündem 4 · Bütçe</span>
                <span className="rounded-full bg-danger/12 px-1.5 text-[10px] font-medium text-danger">
                  Canlı
                </span>
              </div>
              {[
                ['Kabul', 78, 'bg-success'],
                ['Ret', 15, 'bg-danger'],
                ['Çekimser', 7, 'bg-ink-3'],
              ].map(([l, o, r], i) => (
                <div key={l as string} className="mt-1.5">
                  <div className="flex justify-between text-[10px] text-ink-3">
                    <span>{l}</span>
                    <span className="data">%{o}</span>
                  </div>
                  <div className="mt-0.5 h-1 rounded-[3px] bg-line">
                    <motion.i
                      className={`block h-full rounded-[3px] ${r}`}
                      initial={{ width: 0 }}
                      whileInView={{ width: `${o}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.9, ease: YUMUSAK, delay: 0.3 + i * 0.1 }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </YuzenKart>
          <YuzenKart className="-left-3 bottom-8 sm:-left-8">
            <Kesit
              Ikon={BadgeCheck}
              baslik="Hazirun 787"
              metin="salonda 412 · uzaktan 375 · çoğunluk sağlandı"
            />
          </YuzenKart>
        </>
      ),
    },
  ]
  return (
    <div className="space-y-8 pb-8 sm:space-y-0">
      {bloklar.map((b) => (
        <Section key={b.eyebrow} className="py-10 sm:py-16">
          <div
            className={cn(
              'grid items-center gap-12 lg:grid-cols-2 lg:gap-20',
              b.ters && 'lg:[&>*:first-child]:order-2',
            )}
          >
            <Reveal>
              <Eyebrow>{b.eyebrow}</Eyebrow>
              <h2 className="heading mt-4 text-balance text-3xl text-ink sm:text-4xl">
                {b.baslik}
              </h2>
              <p className="mt-4 text-pretty text-base leading-relaxed text-ink-2 sm:text-lg">
                {b.metin}
              </p>
              <ul className="mt-6 space-y-2.5">
                {b.maddeler.map((m) => (
                  <li key={m} className="flex items-start gap-2.5 text-[15px] text-ink">
                    <BadgeCheck className="mt-0.5 size-4 shrink-0 text-brand" aria-hidden />
                    {m}
                  </li>
                ))}
              </ul>
              <Link
                to={`/ozellikler/${b.modul.slug}`}
                className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline"
              >
                {b.modul.ad} sayfasına gidin
              </Link>
            </Reveal>
            <Reveal delay={0.1} y={24} className="px-3 sm:px-8 lg:px-4">
              <FotoKart ustune={b.kesitler}>
                <Foto ad={b.foto} oran="4:3" alt={b.alt} />
              </FotoKart>
            </Reveal>
          </div>
        </Section>
      ))}
    </div>
  )
}

/* ---------------- Nasıl çalışır ---------------- */
function NasilCalisir() {
  const adimlar = [
    {
      Ikon: ScrollText,
      baslik: 'Derneği tanıtın',
      metin:
        'Ad, il, tahmini üye sayısı ve amaç. Panel ve web sitesi aynı anda açılır; kurulum sihirbazı eksikleri gösterir.',
      sure: '5 dakika',
    },
    {
      Ikon: FileSpreadsheet,
      baslik: 'Üyeleri aktarın',
      metin:
        'Excel listenizi yükleyin; önce ön izleme, sonra kayıt. Üye numaralarınız ve özel alanlarınız korunur.',
      sure: '1 saat',
    },
    {
      Ikon: KeyRound,
      baslik: 'Ödemeyi bağlayın',
      metin:
        'Sağlayıcı anahtarınızı girin, havale hesabınızı yazın. İlk aidat dönemini açın; hatırlatmalar gitmeye başlar.',
      sure: 'Aynı gün',
    },
  ]
  return (
    <Section className="bg-surface/60" id="nasil-calisir">
      <Reveal>
        <Baslik
          eyebrow="Nasıl çalışır"
          baslik="Bir öğleden sonra yayında"
          metin="Uzun kurulum projesi yok. Üç adım, sırayla; her adım bitince bir sonrakinin kilidi açılır."
          hizala="center"
        />
      </Reveal>
      <div className="relative mt-12">
        <motion.div
          className="absolute left-[calc(16.66%+20px)] right-[calc(16.66%+20px)] top-6 hidden h-px bg-line md:block"
          aria-hidden
        />
        <Stagger className="grid gap-6 md:grid-cols-3">
          {adimlar.map((a, i) => (
            <StaggerItem key={a.baslik}>
              <div className="relative text-center md:px-4">
                <span className="relative z-10 mx-auto grid size-12 place-items-center rounded-full bg-brand text-on-brand ring-8 ring-page">
                  <a.Ikon className="size-5" />
                </span>
                <p className="mt-5 text-[11px] font-medium uppercase tracking-wider text-ink-3">
                  Adım {i + 1} · {a.sure}
                </p>
                <p className="mt-1 text-lg font-semibold text-ink">{a.baslik}</p>
                <p className="mt-2 text-sm leading-relaxed text-ink-2">{a.metin}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </Section>
  )
}

/* ---------------- Güvenlik ---------------- */
function Guvenlik() {
  const maddeler = [
    {
      Ikon: Lock,
      b: 'Veritabanı seviyesinde izolasyon',
      m: 'Her derneğin verisi satır seviyesinde ayrılır; unutulan bir filtre başka derneğin verisini gösteremez.',
    },
    {
      Ikon: ShieldCheck,
      b: 'İkinci adım doğrulama',
      m: 'Doğrulayıcı uygulamayla altı haneli kod. Bütün yöneticiler için zorunlu kılınabilir.',
    },
    {
      Ikon: ScrollText,
      b: 'Silinemeyen denetim kaydı',
      m: 'Kim, ne zaman, neyi değiştirdi. Denetim kurulu salt okunur görür.',
    },
    {
      Ikon: KeyRound,
      b: 'Şifreli sağlayıcı anahtarları',
      m: 'Ödeme ve SMS anahtarları iki katmanlı şifreyle saklanır; panel yalnızca son dört karakteri gösterir.',
    },
  ]
  return (
    <Section>
      <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr] lg:gap-16">
        <Reveal>
          <Eyebrow>Güvenlik ve KVKK</Eyebrow>
          <h2 className="heading mt-4 text-balance text-3xl text-ink sm:text-4xl">
            Veriniz sizin. Kim ne yaptı, kayıtta.
          </h2>
          <p className="mt-4 text-pretty text-base leading-relaxed text-ink-2 sm:text-lg">
            Güvenlik pazarlama sözü değil, mimari karardır: kiracı ayrımı uygulama koduna değil
            veritabanına emanet edilir, her sürüm otomatik taramadan geçer, verinizi istediğiniz an
            açık biçimde dışa aktarırsınız. Ne yaptığımızı açıkça yazarız; abartmayız.
          </p>
          <Link
            to="/ozellikler/guvenlik-ve-kvkk"
            className="mt-6 inline-flex text-sm font-medium text-brand hover:underline"
          >
            Güvenlik sayfası
          </Link>
        </Reveal>
        <Stagger className="grid gap-4 sm:grid-cols-2">
          {maddeler.map((m) => (
            <StaggerItem key={m.b}>
              <Kart className="h-full p-5">
                <m.Ikon className="size-5 text-brand" aria-hidden />
                <p className="mt-3 font-semibold text-ink">{m.b}</p>
                <p className="mt-1.5 text-sm leading-relaxed text-ink-2">{m.m}</p>
              </Kart>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </Section>
  )
}

/* ---------------- Çözümler ---------------- */
function Cozumler() {
  return (
    <Section className="bg-surface/60">
      <Reveal>
        <Baslik
          eyebrow="Kimler için"
          baslik="Derneğinizin işine göre kurulmuş"
          metin="Meslek derneğinin yılı kongreyle, hemşehri derneğininki bağış kampanyasıyla ölçülür. Her tip için gün içindeki gerçek işi anlattık."
          hizala="center"
        />
      </Reveal>
      <Reveal delay={0.1} className="mt-12 grid gap-4 md:grid-cols-[1.6fr_1fr]">
        <FotoKart
          ustune={
            <YuzenKart className="bottom-4 left-4">
              <Kesit
                Ikon={ScrollText}
                baslik="Yönetim kurulu toplantısı 14"
                metin="3 karar yazıldı · tutanak PDF hazır"
              />
            </YuzenKart>
          }
        >
          <Foto
            ad="toplanti"
            oran="16:9"
            sizes="(min-width: 768px) 60vw, 100vw"
            alt="Ahşap masa etrafında çay eşliğinde toplantı yapan dernek yönetim kurulu"
          />
        </FotoKart>
        <FotoKart
          className="hidden md:block"
          ustune={
            <YuzenKart className="bottom-4 left-4 right-4">
              <div className="flex justify-between text-[11px] text-ink-3">
                <span>Burs kampanyası</span>
                <span className="data">%72</span>
              </div>
              <div className="mt-1.5 h-1.5 rounded-[3px] bg-line">
                <motion.i
                  className="block h-full rounded-[3px] bg-brand"
                  initial={{ width: 0 }}
                  whileInView={{ width: '72%' }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, ease: YUMUSAK, delay: 0.4 }}
                />
              </div>
            </YuzenKart>
          }
        >
          <Foto
            ad="bagis"
            oran="4:3"
            sizes="(min-width: 768px) 36vw, 100vw"
            alt="Mahalle yemeğinde gülen yaşlı bir üye ve yeşil önlüklü genç gönüllü"
            className="h-full"
          />
        </FotoKart>
      </Reveal>
      <Stagger className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {COZUMLER.map((c) => (
          <StaggerItem key={c.slug} className="h-full">
            <Link
              to={`/cozumler/${c.slug}`}
              className="group flex h-full flex-col rounded-[var(--radius-panel)] bg-surface p-5 ring-soft transition-transform duration-300 hover:-translate-y-0.5"
            >
              <c.ikon className="size-5 text-brand" aria-hidden />
              <p className="mt-4 font-semibold text-ink">{c.ad}</p>
              <p className="mt-1.5 text-sm leading-relaxed text-ink-2">{c.kisa}</p>
              <span className="mt-4 text-sm font-medium text-brand">Nasıl çalışır</span>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </Section>
  )
}

/* ---------------- Fiyat özeti ---------------- */
function FiyatOzeti() {
  return (
    <Section>
      <Reveal>
        <Baslik
          eyebrow="Fiyatlandırma"
          baslik="Ücretsiz plan yok; on dört gün deneme var"
          metin="Üye portalı ve online ödeme her planda dahil, eklenti değil. Yıllık ödemede iki ay bizden. Fiyatlar KDV hariçtir."
          hizala="center"
        />
      </Reveal>
      <Stagger className="mt-12 grid gap-4 md:grid-cols-4">
        {PLANLAR.map((p) => (
          <StaggerItem key={p.kod} className="h-full">
            <Kart className={cn('flex h-full flex-col p-5', p.vurgu && 'ring-2 ring-brand')}>
              <div className="flex items-center justify-between">
                <p className="font-semibold text-ink">{p.ad}</p>
                {p.vurgu && <Rozet tone="brand">Yaygın tercih</Rozet>}
              </div>
              <p className="data mt-3 text-2xl font-semibold text-ink">
                {p.aylik ? tl(p.aylik) : 'Görüşmeli'}
                {p.aylik ? <span className="text-sm font-normal text-ink-3"> / ay</span> : null}
              </p>
              <p className="mt-1 text-xs text-ink-3">
                {p.uye ? `${p.uye.toLocaleString('tr-TR')} üyeye kadar` : 'Sınırsız üye'}
              </p>
              <p className="mt-3 text-sm text-ink-2">{p.kimIcin}</p>
            </Kart>
          </StaggerItem>
        ))}
      </Stagger>
      <Reveal className="mt-8 text-center">
        <ButonLink to="/fiyatlandirma" variant="secondary" ok>
          Bütün planlar ve paketler
        </ButonLink>
      </Reveal>
    </Section>
  )
}

/* ---------------- SSS ---------------- */
function Sss() {
  return (
    <Section className="bg-surface/60">
      <div className="grid gap-10 lg:grid-cols-[1fr_1.6fr]">
        <Reveal>
          <Baslik
            eyebrow="Sık sorulanlar"
            baslik="Karar vermeden önce"
            metin="Kurulum, deneme, veri taşıma, ödeme sağlayıcısı ve destek. Cevabı olmayan bir sorunuz varsa yazın; aynı gün döneriz."
          />
          <ButonLink to="/sss" variant="secondary" className="mt-6">
            Bütün sorular
          </ButonLink>
        </Reveal>
        <Reveal delay={0.1}>
          <SssAkordeon sorular={GENEL_SSS.slice(0, 5)} />
        </Reveal>
      </div>
    </Section>
  )
}
